import React, { useState, useEffect } from 'react';
import styles from './transfer.module.css';
import iconoTransferir from '../../assets/icono-transferir-saldo.png';
import iconoSaldo from '../../assets/icono-saldo.png';
import iconoUsuario from '../../assets/icono-usuario.png';
import iconoDisclaimer from '../../assets/icono-disclaimer.png';
import API from '../../../../../utils/api';
import numeral from 'numeral';
import VerificationInput from 'react-verification-input';
import useUserContext from '../../../../../hooks/useUserContext';

export const Transfer = () => {
  const { updateUserBalance, updateUserAvailable } = useUserContext();
  const [step, setStep] = useState('SEARCH_USER');
  const [userToTransfer, setUserToTransfer] = useState(null);
  const [availableBalance, setAvailableBalance] = useState(0);
  const [username, setUsername] = useState('');
  const [amount, setAmount] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [errors, setErrors] = useState({
    username: '',
    amount: '',
  });

  // 'IDLE', 'SENDING_SMS', 'LOADING', 'ERROR_SENDING_SMS', 'ERROR_SMS_CODE'
  const [smsStatus, setSmsStatus] = useState('IDLE');
  const [verificationCode, setVerificationCode] = useState('');
  const [successfullTransaction, setSuccessfullTransaction] = useState(false);

  useEffect(() => {
    // Obtener saldo disponible para transferencias
    const fetchBalance = async () => {
      try {
        setIsLoading(true);
        const response = await API.fetchAvailableCashout();
        if (response?.data?.available) {
          setAvailableBalance(response.data.available);
        }
      } catch (error) {
        console.error('Error al obtener saldo disponible:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBalance();
  }, []);

  const formatCurrency = (value) => {
    if (!value) return '';
    // Remover cualquier carácter que no sea número o punto
    const cleaned = value.toString().replace(/[^\d.]/g, '');
    // Permitir solo un punto decimal
    const parts = cleaned.split('.');
    if (parts.length > 2) {
      return parts[0] + '.' + parts.slice(1).join('');
    }
    // Limitar a 2 decimales
    if (parts[1] && parts[1].length > 2) {
      return parts[0] + '.' + parts[1].substring(0, 2);
    }
    return cleaned;
  };

  const validateUsername = (value) => {
    if (!value.trim()) {
      return 'El nombre de usuario es requerido';
    }
    if (value.trim().length < 2) {
      return 'El nombre de usuario debe tener al menos 2 caracteres';
    }
    return '';
  };

  const validateAmount = (value) => {
    if (!value || value.trim() === '') {
      return 'El monto es requerido';
    }
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue <= 0) {
      return 'El monto debe ser mayor a $0.00';
    }
    if (numValue < 1) {
      return 'El monto mínimo es $1.00 MXN';
    }
    const maxAmount = availableBalance / 100;
    if (numValue > maxAmount) {
      return `El monto máximo es ${numeral(maxAmount).format('$0,000.00')} MXN`;
    }
    return '';
  };

  const handleUsernameChange = (e) => {
    const value = e.target.value;
    setUsername(value);
    setErrors(prev => ({
      ...prev,
      username: validateUsername(value),
    }));
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    const formatted = formatCurrency(value);
    setAmount(formatted);
    setErrors(prev => ({
      ...prev,
      amount: validateAmount(formatted),
    }));
  };

  const handleSearchUser = async () => {
    const usernameError = validateUsername(username);
    const amountError = validateAmount(amount);

    if (usernameError || amountError) {
      setErrors({
        username: usernameError,
        amount: amountError,
      });
      return;
    }

    setIsSearching(true);
    try {
      const response = await API.searchUser(username.trim());

      if (response.data.user === null) {
        setErrors(prev => ({
          ...prev,
          username: 'El usuario no existe',
        }));
        return;
      }

      setStep('CONFIRM_TRANSFER');
      setUserToTransfer(response.data.user);
    } catch (error) {
      setErrors(prev => ({
        ...prev,
        username: 'Error al buscar usuario',
      }));
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearchUser();
    }
  };


  if (step === 'SEARCH_USER') {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.iconContainer}>
            <img src={iconoTransferir} alt="Transferir" className={styles.headerIcon} />
          </div>
          <h1 className={styles.title}>Transferir saldo</h1>
          <h2 className={styles.subtitle}>a otro usuario</h2>
        </div>

        <div className={styles.formContainer}>
          <p className={styles.introText}>
            Transfiere de tu saldo retirable a otros usuarios de Pickwin.
          </p>

          <div className={styles.fieldGroup}>
            <label className={styles.label} htmlFor="availableBalance">
              Saldo disponible para transferencias:
            </label>
            <div className={styles.inputWrapper}>
              <img src={iconoSaldo} alt="Saldo" className={styles.icon} />
              <input
                id="availableBalance"
                type="text"
                className={styles.input}
                value={isLoading ? 'Cargando...' : numeral(availableBalance / 100).format('0,000.00')}
                readOnly
              />
            </div>
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label} htmlFor="username">
              Nombre del usuario a transferir
            </label>
            <div className={`${styles.inputWrapper} ${errors.username ? styles.inputWrapperError : ''}`}>
              <img src={iconoUsuario} alt="Usuario" className={styles.icon} />
              <input
                autoFocus
                id="username"
                type="text"
                className={styles.input}
                placeholder="Ronaldo10"
                value={username}
                onChange={handleUsernameChange}
                onKeyPress={handleKeyPress}
                maxLength={50}
              />
            </div>
            {errors.username && (
              <span className={styles.errorMessage}>{errors.username}</span>
            )}
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label} htmlFor="amount">
              Monto a transferir
            </label>
            <div className={`${styles.inputWrapper} ${errors.amount ? styles.inputWrapperError : ''}`}>
              <img src={iconoSaldo} alt="Monto" className={styles.icon} />
              <input
                id="amount"
                type="text"
                className={styles.input}
                placeholder="100.00"
                value={amount}
                onChange={handleAmountChange}
                onKeyPress={handleKeyPress}
                inputMode="decimal"
              />
            </div>
            {errors.amount && (
              <span className={styles.errorMessage}>{errors.amount}</span>
            )}
          </div>

          <button
            type="button"
            className="payment-button"
            onClick={handleSearchUser}
            disabled={isSearching || isLoading}
          >
            {isSearching ? 'Buscando...' : 'Buscar usuario'}
          </button>
        </div>

        <div className={styles.warningContainer}>
          <img src={iconoDisclaimer} alt="Advertencia" className={styles.warningIcon} />
          <div className={styles.warningText}>
            <p>Únicamente podrás transferir saldo retirable, desde $1.00 MXN hasta {numeral(availableBalance / 100).format('$0,000.00')} MXN</p>
            <p>Podrás realizar una (1) transferencia cada 24 horas.</p>
          </div>
        </div>
      </div>
    );
  }


  const handleCancelTransfer = () => {
    setStep('SEARCH_USER');
    setUserToTransfer(null);
    setSmsStatus('IDLE');
    setVerificationCode('');
    setSuccessfullTransaction(false);
  };

  const handleSMSRequest = async () => {
    setIsLoading(true);
    setSmsStatus('LOADING');
    try {
      const response = await API.requestSMSCodeForTransfer();
      if (!response.data.success) {
        setSmsStatus('ERROR_SENDING_SMS');
        setIsLoading(false);
        return;
      }
      setSmsStatus('SENDING_SMS');
    } catch (error) {
      console.error('Error al enviar SMS:', error);
      setSmsStatus('ERROR_SENDING_SMS');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTransferConfirm = async () => {
    setIsLoading(true);
    setSmsStatus('LOADING');
    try {
      const response = await API.requestTransferConfirm({
        otp: verificationCode,
        user: { id: userToTransfer.id },
        amount: parseFloat(amount) 
      });

      if (!response.data.success) {
        setSmsStatus('ERROR_SMS_CODE');
        setIsLoading(false);
        return;
      }

    //   {
    //     "success": true,
    //     "error": null,
    //     "balance": 2188083,
    //     "availableForCashout": 384858
    // }

      setSuccessfullTransaction(true);
      // Actualizar saldo disponible si está disponible en la respuesta
      if (response.data.availableForCashout !== undefined) {
        setAvailableBalance(response.data.availableForCashout);
      }
      // Actualizar balance del usuario y saldo disponible para cashout
      if (response.data.balance !== undefined) {
        updateUserBalance(response.data.balance);
      }
      if (response.data.availableForCashout !== undefined) {
        updateUserAvailable(response.data.availableForCashout);
      }
    } catch (error) {
      console.error('Error al confirmar transferencia:', error);
      setSmsStatus('ERROR_SMS_CODE');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmTransfer = async () => {
    if (smsStatus === 'IDLE' || smsStatus === 'ERROR_SENDING_SMS') {
      await handleSMSRequest();
    } else if (smsStatus === 'SENDING_SMS' || smsStatus === 'ERROR_SMS_CODE') {
      await handleTransferConfirm();
    }
  };

  const getStatusButtonText = () => {
    if (smsStatus === 'LOADING') {
      return 'Procesando...';
    }
    if (smsStatus === 'IDLE' || smsStatus === 'ERROR_SENDING_SMS') {
      return smsStatus === 'ERROR_SENDING_SMS' ? 'Reenviar SMS' : 'Enviar SMS';
    }
    if (smsStatus === 'SENDING_SMS' || smsStatus === 'ERROR_SMS_CODE') {
      return smsStatus === 'ERROR_SMS_CODE' ? 'Reintentar' : 'Confirmar transferencia';
    }
    return 'Transferir';
  };

  if (step === 'CONFIRM_TRANSFER') {
    if (successfullTransaction) {
      return (
        <div className={styles.container}>
          <div className={styles.confirmContainer}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px', padding: '40px 20px' }}>
              <div style={{ fontSize: '48px', color: '#78bc28' }}>✓</div>
              <h2 className={styles.confirmTitle} style={{ color: '#78bc28' }}>
                Transferencia completada
              </h2>
              <p className={styles.confirmInstruction} style={{ textAlign: 'center' }}>
                Tu transferencia se completó con éxito.
              </p>
              <button
                type="button"
                className={styles.transferButton}
                onClick={handleCancelTransfer}
                style={{ width: '100%', marginTop: '20px' }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.container}>
        <div className={styles.confirmContainer}>
          <h1 className={styles.confirmTitle}>Confirma datos de transferencia</h1>
          <div className={styles.separator}></div>

          <p className={styles.confirmInstruction}>
            Por favor confirma que los datos del usuario al que vas a transferir saldo sean correctos.
          </p>

          <div className={styles.warningMessage}>
            <img src={iconoDisclaimer} alt="Advertencia" className={styles.warningIconInline} />
            <span className={styles.warningTextInline}>
              Las transferencias de saldo son finales e irreversibles.
            </span>
          </div>

          <div className={styles.recipientInfo}>
            <div className={styles.recipientIcon}>
              <div className={styles.recipientIconInner}></div>
            </div>
            <div className={styles.recipientDetails}>
              <div className={styles.recipientName}>{userToTransfer?.nick || 'Usuario'}</div>
              <div className={styles.recipientAmount}>
                {numeral(parseFloat(amount || 0)).format('$0,000.00')} MXN
              </div>
            </div>
          </div>

          <p className={styles.disclaimerText}>
            Este monto pasará a ser saldo no retirable en la cuenta que reciba la transferencia.
          </p>

          {smsStatus === 'ERROR_SENDING_SMS' && (
            <div className={styles.errorMessage} style={{ textAlign: 'center', padding: '12px', backgroundColor: 'rgba(250, 47, 47, 0.1)', borderRadius: '8px', border: '1px solid #fa2f2f' }}>
              Lo sentimos, encontramos un error al enviar el SMS. Por favor intenta de nuevo.
            </div>
          )}

          {(smsStatus === 'SENDING_SMS' || smsStatus === 'ERROR_SMS_CODE') && (
            <div className={styles.smsPanel}>
              <p className={styles.smsInstruction}>
                Te enviaremos un código por SMS para verificar la transferencia.
              </p>

              {smsStatus === 'ERROR_SMS_CODE' && (
                <div className={styles.errorMessage} style={{ textAlign: 'center', padding: '12px', backgroundColor: 'rgba(250, 47, 47, 0.1)', borderRadius: '8px', border: '1px solid #fa2f2f', marginBottom: '16px' }}>
                  El código es incorrecto o ya no es válido
                </div>
              )}

              <div className={styles.verificationInputContainer}>
                <VerificationInput
                  placeholder=""
                  validChars="0-9"
                  inputProps={{ type: 'tel' }}
                  length={5}
                  autoFocus
                  value={verificationCode}
                  onChange={setVerificationCode}
                  classNames={{
                    container: styles.smsContainer,
                    character: styles.character,
                    characterInactive: styles.characterInactive,
                    characterSelected: styles.characterSelected
                  }}
                />
              </div>
            </div>
          )}

          <div className={styles.confirmButtons}>
            <button
              type="button"
              className="payment-button--danger"
              onClick={handleCancelTransfer}
              disabled={isLoading}
              style={{ width: '50%' }}
            >
              Cancelar
            </button>
            <button
              type="button"
              className={styles.transferButton}
              onClick={handleConfirmTransfer}
              disabled={isLoading || (smsStatus === 'SENDING_SMS' && verificationCode.length !== 5)}
            >
              {getStatusButtonText()}
            </button>
          </div>
        </div>
      </div>
    );
  }

};
